import {
  useMemo, useState, useEffect, useCallback,
} from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { actions } from '../../ShedManagementState'
import { Product } from '../../ShedManagementTypes'
import { CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox } from '@mui/icons-material'
import { Typography } from '@mui/material'
import CheckboxBooleanAttributes from '../CheckboxBooleanAttributes/CheckboxBooleanAttributes'
import ContainerCharacteristics from '../ContainerCharacteristics/ContainerCharacteristics'
import ContainerCheckbox from '../ContainerCheckbox/ContainerCheckbox'
import MainContainer from '../MainContainer/MainContainer'
import ConfirmOrRejectModal from '../../../base/components/ConfirmOrRejectModal'
import ProductsTable from '../ProductsTable/ProductsTable'
import GenericRadioGroup from '../GenericRadioGroup/GenericRadioGroup'
import ErrorLog from '../../../../components/ErrorLog/ErrorLog'
import InformationCaption from '../InformationCaption/InformationCaption'
import * as S from './FormUpdateAttributesProducts.styles'
import { SelectMui } from 'src/components/SelectMui'

const dueDateTypeToIdDict = {
  REGULAR: 1,
  LONG_DURATION: 2,
  INDETERMINATE: 3,
}
const dueDateTypeDict = {
  1: 'REGULAR',
  2: 'LONG_DURATION',
  3: 'INDETERMINATE',
}
const typesDueDate = [
  { description: 'Normal', id: 1 },
  { description: 'Longa Duração', id: 2 },
  { description: 'Indeterminada', id: 3 },
]

const FormUpdateAttributesProducts = () => {
  const [historySelectedValue, setHistorySelectedValue] = useState<Record<string | number, number>>({})
  const [openModalConfirm, setOpenModalConfirm] = useState(false)
  const {
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      idDueDateType: -1,
    },
  })
  const { attributesGroup, selectedProducts, productAttributeDiscrepancyErrors } = useSelector((state) => state.shedManagement)
  const dispatch = useDispatch()

  const rows = useMemo(() =>
    productAttributeDiscrepancyErrors.map(({
      productCode, missingTypes,
    }) => ({
      productCode, missingTypes,
    })), [productAttributeDiscrepancyErrors])

  const updateSelectedProducts = useCallback((selectedItems: Product[]) => {
    dispatch(actions.setSelectedProducts({ selectedProducts: selectedItems }))
  }, [dispatch])

  useEffect(() => {
    let hasChange = false
    const itemsUpdated = selectedProducts.map((item) => {
      if (!item.dueDateType) {
        hasChange = true
        return { ...item, dueDateType: 'REGULAR' }
      }
      return item
    })

    if (hasChange) updateSelectedProducts(itemsUpdated)
  }, [selectedProducts, updateSelectedProducts])

  const onSubmit = () => {
    const updatedProducts = selectedProducts.map((product) => {
      const historyValue = historySelectedValue[product.idModel]
      if (product.dueDateType === undefined && historyValue !== undefined && historyValue !== -1) {
        const dueDateTypeString = dueDateTypeDict[historyValue]
        return { ...product, dueDateType: dueDateTypeString }
      }
      return product
    })
    dispatch(actions.saveAttributesProducts(updatedProducts))
    setOpenModalConfirm(false)
  }

  const idDueDateType = watch('idDueDateType')
  const handleDueDateTypeChange = (event) => {
    const newId = event.target.value
    setValue('idDueDateType', newId)
    if (newId !== undefined && selectedProducts.length > 0) {
      const dueDateTypeString = dueDateTypeDict[newId]
      const products = selectedProducts.map((item) => ({
        ...item,
        dueDateType: dueDateTypeString,
      }))
      updateSelectedProducts(products)
    }
  }

  useEffect(() => {
    let differentDueDateTypes = false
    setHistorySelectedValue((prevState) => {
      const newState: Record<string | number, number> = { ...prevState }
      selectedProducts.forEach((product) => {
        if (product.dueDateType && product.idModel !== undefined) {
          newState[product.idModel] = dueDateTypeToIdDict[product.dueDateType]
        } else if (!product.dueDateType && product.idModel) {
          newState[product.idModel] = 1
        }
      })
      return newState
    })

    selectedProducts.forEach((product) => {
      if (selectedProducts[0].dueDateType !== product.dueDateType) {
        differentDueDateTypes = true
      }
    })

    if (!differentDueDateTypes) {
      if (selectedProducts[0]?.dueDateType === null) {
        setValue('idDueDateType', 1)
        return
      }
      setValue('idDueDateType', dueDateTypeToIdDict[selectedProducts[0]?.dueDateType])
    } else {
      setValue('idDueDateType', -1)
    }
  }, [selectedProducts, setValue])

  return (
    <MainContainer
      onSubmit={onSubmit}
      handleSubmit={handleSubmit}
    >
      <S.Container>
        <ProductsTable />
        <ContainerCharacteristics label='Características' width='34%'>
          {
            attributesGroup.map((group) => (
              <GenericRadioGroup key={group.groupName} attributeGroup={group} />
            ))
          }
          <S.ContainerCheckboxesOptions>
            <ContainerCheckbox
              label='Permite alta?'
              inlined
              testId='checkbox-allow-high-storage-test-id'
            >
              <CheckboxBooleanAttributes
                label='Sim'
                property='allowHighStorage'
                items={selectedProducts}
                updateSelectedItems={updateSelectedProducts}
              />
            </ContainerCheckbox>
            <ContainerCheckbox
              label='Etiquetar no fracionamento?'
              inlined
              testId='checkbox-fractioning-tag-test-id'
            >
              <CheckboxBooleanAttributes
                label='Sim'
                property='fractioningTag'
                items={selectedProducts}
                updateSelectedItems={updateSelectedProducts}
              />
            </ContainerCheckbox>
            <SelectMui
              name='idDueDateType'
              inputProps={{ ...register('idDueDateType') }}
              autoComplete='off'
              id='lasting-due-date-type'
              label='Tipo de Validade'
              variant='outlined'
              options={typesDueDate}
              optionNone={false}
              value={idDueDateType || ''}
              type='text'
              fullWidth
              maxHeight='600px'
              dataTestId='lasting-due-date-type-test-id'
              onChange={handleDueDateTypeChange}
            />
          </S.ContainerCheckboxesOptions>
          <S.CustomButton
            title='Salvar'
            type='button'
            onClick={() => setOpenModalConfirm(true)}
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            data-testid='save-product-attributes'
            disabled={selectedProducts.length < 1}
          />
        </ContainerCharacteristics>
        <InformationCaption
          moreInformation={(
            <>
              <Typography>
                Ao selecionar produtos, que tiverem atributos
                de tipo, função ou armazenagem diferentes,
                fará com que os campos fiquem sem nenhuma seleção.
              </Typography>
              <Typography>
                Para os campos citados acima, é obrigatório selecionar um atributo.
              </Typography>
              <Typography>
                <strong>Permite alta?</strong>  -
                Se o produto pode ser armazenado no alto, com auxílio de empilhadeira.
              </Typography>
              <Typography>
                <strong>Etiquetar no fracionamento?</strong>  -
                Se o produto deve ser etiquetado durante fracionamento.
              </Typography>
              <Typography>
                <strong>Tipo de Validade</strong>  -
                Define o tipo de validade que o produto terá durante o recebimento.
              </Typography>
              <Typography>
                <u> 1. Normal </u> - Validade padrão do produto.
              </Typography>
              <Typography>
                <u> 2. Longa Duração </u> - O produto possui uma validade que será no formato mês/ano apenas.
              </Typography>
              <Typography>
                <u> 3. Indeterminada </u> - O sistema deve atribuir automaticamente uma validade muita longa para o produto durante o recebimento.
              </Typography>
              <Typography>
                <u> 4. Vazio </u> - Se a opção estiver vazia, a validade será mantida como estava antes da atualização em massa.
              </Typography>
            </>
          )}
        />
        <ConfirmOrRejectModal
          show={openModalConfirm}
          title='Confirmação de atributos'
          customizableDescription={(
            <S.ContainerDescription>
              <S.ContainerItems>
                <S.Item>
                  <p>Os atributos escolhidos, serão aplicados em todos os produtos selecionados</p>
                </S.Item>
                <S.Item>
                  <p>Atenção para os estados das opções <strong>Permite alta, Etiquetar no fracionamento, Validade indeterminada</strong>:</p>
                </S.Item>
                <S.Item>
                  <CheckBox color='info'/>
                  <p>Será aplicado em todos os produtos selecionados.</p>
                </S.Item>
                <S.Item>
                  <IndeterminateCheckBox color='info'/>
                  <p>Será mantida a configuração original.</p>
                </S.Item>
                <S.Item>
                  <CheckBoxOutlineBlank color='info'/>
                  <p>Será removido de todos os produtos selecionados.</p>
                </S.Item>
              </S.ContainerItems>
              <p>
                Deseja prosseguir?
              </p>
            </S.ContainerDescription>
          )}
          onConfirm={onSubmit}
          onReject={() => setOpenModalConfirm(false)}
          onClose={() => setOpenModalConfirm(false)}
          dataTestId='modal-confirmation-attributes'
          size='lg'
        />
      </S.Container>
      {
        productAttributeDiscrepancyErrors.length > 0
          && (
            <ErrorLog
              label='Logs'
              columns={[
                { id: 'productCode', label: 'Código do produto' },
                { id: 'missingTypes', label: 'Atributos em falta' },
              ]}
              rows={rows}
              width='38.6%'
            />
          )
      }
    </MainContainer>
  )
}

export default FormUpdateAttributesProducts
